import { apiClient } from './api';

export type PlanSlug = 'lite' | 'starter' | 'professional' | 'enterprise';
export type BillingCycle = 'monthly' | 'yearly';

export interface PlanPricing {
  monthly: number;
  yearly: number;
}

export interface PlanLimits {
  max_stores: number | null;
  max_users: number | null;
  max_products: number | null;
}

export interface Plan {
  _id: string;
  name: string;
  display_name: string;
  slug: string;
  tier: number;
  pricing: PlanPricing;
  limits: PlanLimits;
  features: string[];
  is_active: boolean;
  sort_order: number;
  tenant_count: number;
  created_at?: string;
  updated_at?: string;
}

export interface CreatePlanDto {
  name: string;
  display_name: string;
  slug: string;
  tier: number;
  pricing: PlanPricing;
  limits?: PlanLimits;
  features?: string[];
  is_active?: boolean;
  sort_order?: number;
}

export type UpdatePlanDto = Partial<Omit<CreatePlanDto, 'name' | 'slug' | 'tier'>>;

export interface ListPlansQuery {
  active_only?: boolean;
}

export interface PlansResponse {
  success: true;
  data: Plan[];
}

export interface PlanResponse {
  success: true;
  data: Plan;
}

export interface PlanTenant {
  subscription_id: string;
  billing_cycle: BillingCycle;
  status: 'trial' | 'active' | 'grace' | 'suspended' | 'cancelled';
  current_period_end: string;
  tenant: {
    _id: string;
    name?: string;
    email?: string;
    status?: string;
    [key: string]: unknown;
  } | null;
}

export interface PlanTenantsResponse {
  success: true;
  data: PlanTenant[];
  meta: {
    page: number;
    limit: number;
    total: number;
    total_pages: number;
  };
}

export class PlanService {
  static async listPlans(query?: ListPlansQuery): Promise<Plan[]> {
    const response = await apiClient.get<PlansResponse>('/plans', {
      params: query,
    });
    return response.data.data;
  }

  static async getPlanById(id: string): Promise<Plan> {
    const response = await apiClient.get<PlanResponse>(`/plans/${id}`);
    return response.data.data;
  }

  static async createPlan(dto: CreatePlanDto): Promise<Plan> {
    const response = await apiClient.post<PlanResponse>('/plans', dto);
    return response.data.data;
  }

  static async updatePlan(id: string, dto: UpdatePlanDto): Promise<Plan> {
    const response = await apiClient.put<PlanResponse>(`/plans/${id}`, dto);
    return response.data.data;
  }

  static async activatePlan(id: string): Promise<Plan> {
    const response = await apiClient.put<PlanResponse>(
      `/plans/${id}/activate`,
    );
    return response.data.data;
  }

  static async deactivatePlan(id: string): Promise<Plan> {
    const response = await apiClient.put<PlanResponse>(
      `/plans/${id}/deactivate`,
    );
    return response.data.data;
  }

  static async getTenantsByPlan(
    id: string,
    page = 1,
    limit = 20,
  ): Promise<PlanTenantsResponse> {
    const response = await apiClient.get<PlanTenantsResponse>(
      `/plans/${id}/tenants`,
      { params: { page, limit } },
    );
    return response.data;
  }

  static async deletePlan(id: string): Promise<void> {
    await apiClient.delete(`/plans/${id}`);
  }
}

export default PlanService;
